import { localizeDeletedAccounts } from './deletedAccounts';
import { isUpdateRequired, markUpdateRequired } from './updateRequired';
import type {
  AppMode,
  AuthResponse,
  CallConfig,
  ChatSettings,
  EnvelopeVersion,
  GatheringReply,
  GatheringState,
  HealthErrors,
  HealthOverview,
  HealthWindow,
  IdeaStatus,
  MessageReceipts,
  PollState,
  QrDeviceKind,
  ReportReason,
  ScheduledMessage,
  SignedInSession,
  SignInEventKind,
} from '@twinden/shared';
import type { OpenedApp, OpenedE2EE } from './e2ee/session';
import type { QueuedRequest } from '../store/chat';

const API_BASE = '/api';

type RequestOptions = {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  headers?: Record<string, string>;
  /** Give up after this long; a timeout counts as a network error (D33). */
  timeoutMs?: number;
  /**
   * Skip the E2EE response transform: the key layer's own reads (M73), which
   * must not wait on opening what they carry.
   */
  raw?: boolean;
};

// Normalise non-2xx responses into ApiError so every page shows the same,
// actionable message. 429 gets a stable code and friendly copy regardless of
// the edge proxy's wording.
async function toApiError(response: Response): Promise<ApiError> {
  const body = await response.json().catch(() => ({}));
  const status = response.status;
  let code = body.error?.code || 'UNKNOWN_ERROR';
  let message = body.error?.message || 'Request failed';
  // A newer server asks this app to update (any 426, #82): the banner.
  if (isUpdateRequired(status)) markUpdateRequired(body.error?.message);

  // The family link's "try later" answers keep their own code (D155, D156),
  // and so do the family handle's 3-in-30-days cap (D216) and the device
  // keys' 10-a-day cap (D262), which the key store banner names.
  if (
    status === 429 &&
    code !== 'TRY_LATER' &&
    code !== 'TOO_MANY_REQUESTS' &&
    code !== 'HANDLE_CHANGE_LIMIT' &&
    code !== 'TOO_MANY_DEVICES'
  ) {
    code = 'RATE_LIMITED';
    message = 'Too many attempts. Please wait a moment and try again.';
  }

  // A taken handle comes back with a fresh suggestion (D147), either beside
  // the error or inside it.
  const suggestion = body.suggestion ?? body.error?.suggestion;
  const retryAt =
    typeof body.error?.retryAt === 'string' ? body.error.retryAt : undefined;
  const error = new ApiError(message, code, status, suggestion, retryAt);
  // E.g. the stored revision with STALE_REVISION (M76, D326).
  if (body.error?.details && typeof body.error.details === 'object') {
    error.details = body.error.details as Record<string, unknown>;
  }
  return error;
}

// How long a send waits for the server before it counts as not sent (D33).
const SEND_TIMEOUT_MS = 20_000;

function networkError(): ApiError {
  return new ApiError(
    'Cannot reach the server. Check your connection and try again.',
    'NETWORK_ERROR',
    0
  );
}

// ---- Family / invite / device types (identity spec §7.3–§7.7) ----

/** A fun handle with the signed token that lets an admin use it (D147). */
export interface HandleSuggestion {
  handle: string;
  token: string;
}

/** The family link as I see it (D152); url and QR are null while it's off. */
export interface JoinLink {
  enabled: boolean;
  url: string | null;
  qrDataUrl: string | null;
}

/** Someone asking to join with the family link (D153, D154). */
export interface JoinRequest {
  id: string;
  user: {
    id: string;
    handle: string;
    displayName: string;
    avatarUrl: string | null;
  };
  note: string | null;
  sharedBy: { id: string; displayName: string } | null;
  createdAt: string;
}

export type FamilyRole = 'root' | 'admin' | 'member';

/** My own request to join a family (D153). */
export interface MyJoinRequest {
  id: string;
  familyId: string;
  familyName: string;
  familyHandle: string;
  status: 'pending' | 'approved' | 'declined' | 'cancelled';
  createdAt: string;
}

/** What the family link shows before you join (D153, D156). */
export interface JoinLinkInfo {
  familyId: string;
  familyName: string;
  familyHandle: string;
  sharedBy: string | null;
}

/** A new account made with the family link (D153). */
export interface JoinNewAccountBody {
  displayName: string;
  handle: string;
  handleToken: string;
  note?: string;
  via?: string;
  deviceName?: string;
  pendingUserId: string;
  response: unknown;
}

export interface FamilyListItem {
  /** M28 part 2: an encrypted record, opened on the device (D65). */
  sealed?: string | null;
  e2eeApp?: OpenedApp;
  id: string;
  name: string;
  /** Tells families with the same name apart (M59, D217). */
  handle: string;
  description: string | null;
  avatarUrl: string | null;
  role: FamilyRole;
  nickname: string | null;
  joinedAt: string;
  createdAt: string;
  memberCount: number;
}

/** The admin panel's Review access tab (M56, D202–D205). */
export interface AdminReview {
  family: { id: string; name: string; createdAt: string } | null;
  members: Array<{
    id: string;
    handle: string;
    displayName: string;
    role: 'root' | 'admin' | 'member';
    lastSeenAt: string | null;
  }>;
  links: Array<{
    id: string;
    userId: string;
    displayName: string;
    label: string | null;
    createdAt: string;
    expiresAt: string;
    revokedAt: string | null;
    lastUsedAt: string | null;
    status: 'active' | 'expired' | 'revoked';
    activeSessions: number;
  }>;
}

export interface FamilyMember {
  id: string;
  userId: string;
  role: FamilyRole;
  nickname: string | null;
  joinedAt: string;
  user: {
    id: string;
    handle: string;
    displayName: string;
    avatarUrl: string | null;
    lastSeenAt: string | null;
    /** Their birthday, if they added it (D144). */
    birthDay?: number | null;
    birthMonth?: number | null;
  };
}

export interface FamilyDetail {
  id: string;
  name: string;
  handle: string;
  /** Roots only: handle changes left in the 30-day window (D216). */
  handleChangesLeft?: number;
  description: string | null;
  avatarUrl: string | null;
  createdById: string | null;
  createdAt: string;
  updatedAt: string;
  members: FamilyMember[];
  currentUserRole: FamilyRole;
  currentUserNickname: string | null;
}

export interface EnrollmentTokenRow {
  id: string;
  familyId: string;
  kind: 'enroll' | 'add_device';
  handle: string | null;
  displayName: string | null;
  userId: string | null;
  familyRole: FamilyRole;
  status: 'pending' | 'consumed' | 'revoked' | 'expired';
  invitedBy: string | null;
  createdBySystemRoot: boolean;
  consumedBy: string | null;
  createdAt: string;
  expiresAt: string;
  targetUser?: {
    id: string;
    handle: string;
    displayName: string;
  } | null;
}

export interface CreatedEnrollmentToken {
  token: EnrollmentTokenRow;
  joinUrl: string;
  qrDataUrl: string;
}

export interface FamilyInviteRow {
  id: string;
  familyId: string;
  targetUserId: string;
  familyRole: FamilyRole;
  invitedBy: string;
  status: 'pending' | 'accepted' | 'declined' | 'revoked' | 'expired';
  createdAt: string;
  respondedAt: string | null;
  expiresAt: string;
  targetUser?: { id: string; handle: string; displayName: string };
}

/** Someone from another of my families (D350). */
export interface PersonYouKnow {
  id: string;
  handle: string;
  displayName: string;
  avatarUrl: string | null;
  /** A family we share: the face opens through it (D292). */
  viaFamilyId: string;
}

export interface MyInvite extends FamilyInviteRow {
  family: {
    id: string;
    name: string;
    handle?: string;
    avatarUrl: string | null;
  };
  invitedByUser: { id: string; handle: string; displayName: string };
}

export interface AuditEntry {
  id: string;
  actorUserId: string | null;
  actorIsSystemRoot: boolean;
  action: string;
  targetType: string | null;
  targetId: string | null;
  familyId: string | null;
  metadata: Record<string, unknown> | null;
  createdAt: string;
  // A deleted account has no handle (D173).
  actor?: {
    id: string;
    handle: string | null;
    displayName: string;
    deleted?: true;
  } | null;
}

/** One month of Twinden's costs, as published (M43, D113). */
export interface CostSummary {
  month: string;
  estimated: boolean;
  note: string | null;
  items: Array<{
    label: string;
    detail: string | null;
    amount: number;
    upcoming: boolean;
  }>;
  total: number;
  received: number;
  /** How many families chipped in (M55, D196); null when not counted. */
  familiesCount: number | null;
  carriedIn: number;
  balance: number;
  coveredThrough: string | null;
  history: Array<{ month: string; total: number; received: number }>;
}

/** Someone asking for Twinden for their family (M44, D114). */
export interface InviteRequest {
  id: string;
  name: string;
  email: string;
  familySize: string;
  note: string | null;
  status: 'new' | 'invited' | 'declined';
  adminNote: string | null;
  handledAt: string | null;
  createdAt: string;
}

/** A month as the system root edits it (M43, D109). */
export interface CostMonth {
  month: string;
  received: number;
  /** Families who chipped in (D196); null when not counted. */
  familiesCount: number | null;
  estimated: boolean;
  note: string | null;
  items: Array<{
    label: string;
    detail: string | null;
    amount: number;
    upcoming: boolean;
  }>;
}

/** An idea on Shape Twinden (M55, D198): never says who posted it. */
export interface Idea {
  id: string;
  title: string;
  body: string | null;
  status: IdeaStatus;
  statusNote: string | null;
  votesCount: number;
  mergedCount: number;
  doneAt: string | null;
  createdAt: string;
  updatedAt: string;
  /** Signed in only. */
  votedByMe?: boolean;
  /** Signed in only: you posted it (to edit within 15 minutes). */
  mine?: boolean;
}

/** An idea as the system root sees it (D198, D200). */
export interface AdminIdea extends Idea {
  hiddenAt: string | null;
  mergedIntoId: string | null;
  voterFamiliesCount: number;
  author: {
    handle: string;
    displayName: string;
    deleted: boolean;
    familiesCount: number;
  } | null;
}

export interface SiteConfig {
  supportUrl: string | null;
  /** Contact Twinden (M58, D209); null hides it. */
  supportEmail?: string | null;
  /** The native apps' store pages and the direct APK (D344); null hides each. */
  appStoreUrl?: string | null;
  playStoreUrl?: string | null;
  androidApkUrl?: string | null;
  costs: CostSummary | null;
}

// Report, block and contact (M58, docs/report-block-contact.md).

/** Someone in a report or the blocked list; null when their row is gone. */
export interface ReportPerson {
  id: string;
  handle: string | null;
  displayName: string;
  avatarUrl: string | null;
  deleted?: boolean;
}

/** A report as a family's root sees it (D207): ids, reason, note. */
export interface FamilyReport {
  id: string;
  reason: ReportReason;
  note: string | null;
  conversationId: string | null;
  messageId: string | null;
  handledAt: string | null;
  createdAt: string;
  reporter: ReportPerson | null;
  reported: ReportPerson | null;
}

/** A report as the system root sees it (D210). */
export interface AdminReport extends FamilyReport {
  family: { id: string; name: string };
  shownToFamily: boolean;
  handledBySystemRoot: boolean;
}

export interface BlockedPerson {
  user: ReportPerson;
  createdAt: string;
}

export interface ReportInput {
  reportedUserId: string;
  reason: ReportReason;
  note?: string | null;
  conversationId?: string | null;
  messageId?: string | null;
  block?: boolean;
}

export interface Device {
  id: string;
  deviceName: string | null;
  transports: string[] | null;
  createdAt: string;
  lastUsedAt: string | null;
}

export interface UploadOptions {
  /** 0-1 as bytes are sent. */
  onProgress?: (fraction: number) => void;
  signal?: AbortSignal;
  /**
   * Which family (Documents) or chat the file is for. The server checks the
   * uploader belongs there and refuses plaintext into an encrypted one before
   * storing anything (M28).
   */
  scope?: { familyId?: string; conversationId?: string };
}

// No upload progress for this long means the connection is stuck.
const UPLOAD_STALL_MS = 45_000;
// ...and once every byte is sent, how long the server may take to store it.
const UPLOAD_STORE_MS = 120_000;

export interface UploadedAttachment {
  url: string;
  fileName: string;
  fileSize: number;
  mimeType: string;
  width?: number;
  height?: number;
  duration?: number;
  thumbnailUrl?: string;
}

// ---- Web Push (M17) ----

export interface PushConfig {
  enabled: boolean;
  publicKey: string | null;
}

/**
 * A group's description as it arrives (D234): sealed like a message, and
 * opened in place by the E2EE layer, so `content` is its text once opened.
 */
export interface SealedDescription {
  /** 'v2' (M76): bound in the group domain, with `kind: 'description'`. */
  encryption: 'v1' | 'v2';
  kind?: 'description';
  conversationId: string;
  clientId: string;
  keyEpoch: number;
  content: string;
  /** Set on this device only (D347): `envelope` once it opened here. */
  e2ee?: { locked?: boolean; envelope?: unknown };
}

export interface PushPreferences {
  messages: boolean;
  familyApps: boolean;
  /** Incoming and missed calls (M24, D40). */
  calls: boolean;
  /** Gathering reminders and "Come now" rings (M71, D286). */
  gatherings?: boolean;
  mutedFamilyIds: string[];
  mutedConversationIds: string[];
  /** When each timed chat mute ends (D233); none means Always. */
  mutedConversationUntil?: Record<string, string>;
}

export interface PushSubscriptionRow {
  id: string;
  userAgent: string | null;
  createdAt: string;
  lastUsedAt: string | null;
}

// ---- Family apps (family-apps spec §3–§6) ----

/** A preview's picture, shrunk and sealed by the sender (D318). */
export interface SealedPreviewImage {
  mime: string;
  /** Base64 of the picture's bytes. */
  data: string;
  width: number;
  height: number;
}

export interface LinkPreview {
  status: 'ok' | 'failed';
  url: string;
  title: string | null;
  description: string | null;
  /**
   * The site's own picture address. Never loaded (D320): older previews
   * carry it, newer ones have null and `image` instead.
   */
  imageUrl: string | null;
  siteName: string | null;
  image?: SealedPreviewImage;
}

export interface FamilyLink {
  /** M28 part 2: an encrypted record, opened on the device (D65). */
  sealed?: string | null;
  e2eeApp?: OpenedApp;
  id: string;
  familyId: string;
  urlHash?: string;
  url: string;
  note: string | null;
  title: string | null;
  /** Never loaded (D320); see LinkPreview. */
  imageUrl: string | null;
  siteName: string | null;
  /** The sealed copy of the page's picture (D318). */
  image?: SealedPreviewImage;
  addedBy: string;
  messageId: string | null;
  createdAt: string;
  alreadySaved?: boolean;
  addedByUser?: {
    id: string;
    handle: string;
    displayName: string;
    avatarUrl: string | null;
  } | null;
}

export interface FamilyDocument {
  /** M28 part 2: an encrypted record, opened on the device (D65). */
  sealed?: string | null;
  e2eeApp?: OpenedApp;
  id: string;
  familyId: string;
  fileName: string;
  fileSize: number;
  mimeType: string;
  url: string;
  description: string | null;
  uploadedBy: string;
  messageId: string | null;
  // M26 (D49)
  folder?: string | null;
  tags?: string[];
  createdAt: string;
  uploadedByUser?: {
    id: string;
    handle: string;
    displayName: string;
    avatarUrl: string | null;
  } | null;
}

export interface FamilyList {
  /** M28 part 2: an encrypted record, opened on the device (D65). */
  sealed?: string | null;
  e2eeApp?: OpenedApp;
  id: string;
  familyId: string;
  name: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  itemCount: number;
  checkedCount: number;
  createdByUser?: {
    id: string;
    handle: string;
    displayName: string;
    avatarUrl: string | null;
  } | null;
}

export interface FamilyListItem {
  id: string;
  listId: string;
  text: string;
  note: string | null;
  checked: boolean;
  position: number;
  addedBy: string;
  checkedBy: string | null;
  checkedAt: string | null;
  createdAt: string;
  updatedAt: string;
  addedByUser?: {
    id: string;
    handle: string;
    displayName: string;
    avatarUrl: string | null;
  } | null;
  checkedByUser?: {
    id: string;
    handle: string;
    displayName: string;
    avatarUrl: string | null;
  } | null;
  // M26 (D45–D47)
  assignedTo?: string | null;
  dueDate?: string | null;
  repeat?: 'daily' | 'weekly' | 'monthly' | null;
  lastDoneAt?: string | null;
  lastDoneBy?: string | null;
  assignedToUser?: {
    id: string;
    handle: string;
    displayName: string;
    avatarUrl: string | null;
  } | null;
  lastDoneByUser?: {
    id: string;
    handle: string;
    displayName: string;
    avatarUrl: string | null;
  } | null;
}

// M26 (D48)
export interface ListTemplate {
  /** M28 part 2: an encrypted record, opened on the device (D65). */
  sealed?: string | null;
  e2eeApp?: OpenedApp;
  id: string;
  familyId: string;
  name: string;
  items: Array<{
    text: string;
    note: string | null;
    repeat: string | null;
    // Sealed items (D65), bound to their own id since M76 (D329).
    id?: string;
    sealed?: string | null;
    e2eeApp?: OpenedApp;
  }>;
  createdBy: string;
  createdAt: string;
}

export interface FamilyListDetail extends FamilyList {
  items: FamilyListItem[];
}

export interface ListCounts {
  total: number;
  checked: number;
}

// Mentions inbox (M25, D42).
export interface MentionItem {
  message: {
    id: string;
    conversationId: string;
    sender: {
      id: string;
      handle: string;
      displayName: string;
      avatarUrl: string | null;
    } | null;
    content: string;
    type: string;
    mentions: string[];
    mentionsEveryone: boolean;
    createdAt: string;
    /** Set by this device's opener only (D347); `bound: false` is older (D328). */
    e2ee?: OpenedE2EE;
  };
  conversation: {
    id: string;
    type: 'direct' | 'group';
    name: string | null;
    isDefault: boolean;
  } | null;
  unread: boolean;
}

// Call history (Living Room "Calls"), from M19 call summaries.
export interface CallHistoryEntry {
  id: string;
  conversationId: string;
  conversation: {
    type: 'direct' | 'group';
    name: string;
    isDefault: boolean;
    avatarUrl: string | null;
  };
  callType: 'voice' | 'video';
  outcome: 'completed' | 'declined' | 'cancelled' | 'missed';
  durationSeconds: number;
  /** You placed the call. */
  outgoing: boolean;
  caller: { id: string; displayName: string; avatarUrl: string | null };
  createdAt: string;
}

export interface StarredMessage {
  starredAt: string;
  message: {
    id: string;
    conversationId: string;
    sender: {
      id: string;
      handle: string;
      displayName: string;
      avatarUrl: string | null;
    } | null;
    content: string;
    type: string;
    metadata: Record<string, unknown> | null;
    /** M64: a disappearing message's time; its star goes with it (D238). */
    expiresAt?: string | null;
    createdAt: string;
    /** M28: opened on the device (lib/e2ee). */
    e2ee?: OpenedE2EE;
  };
  conversation: {
    id: string;
    type: string;
    name: string | null;
    isDefault: boolean;
  };
}

const REFRESH_LOCK = 'twinden-refresh';

/** A token the auth store keeps in localStorage (as JSON), or null. */
function readStoredToken(key: string): string | null {
  try {
    const raw = localStorage.getItem(key);
    const value = raw ? (JSON.parse(raw) as unknown) : null;
    return typeof value === 'string' && value ? value : null;
  } catch {
    return null;
  }
}

class ApiClient {
  private accessToken: string | null = null;
  private refreshToken: string | null = null;
  private onTokenRefresh?: (tokens: {
    accessToken: string;
    refreshToken: string;
  }) => void;
  private onAuthError?: () => void;
  private onAccountDeleted?: () => void;
  // M28: opens end-to-end encrypted messages in every response, so the rest
  // of the app sees them as ordinary messages.
  // It gets the request's address too: a family's records open only in the
  // family it names (M76, D329).
  private responseTransform:
    | ((data: unknown, context: { url?: string }) => Promise<unknown>)
    | null = null;

  setResponseTransform(
    transform:
      | ((data: unknown, context: { url?: string }) => Promise<unknown>)
      | null
  ) {
    this.responseTransform = transform;
  }

  private async finish<T>(response: Response, raw = false): Promise<T> {
    // No body (e.g. deleting your account answers 204).
    if (response.status === 204) return undefined as T;
    // Deleted accounts are named in the reader's language (M51).
    const data = localizeDeletedAccounts((await response.json()) as unknown);
    return (
      this.responseTransform && !raw
        ? await this.responseTransform(data, { url: response.url })
        : data
    ) as T;
  }

  setTokens(accessToken: string | null, refreshToken: string | null) {
    this.accessToken = accessToken;
    this.refreshToken = refreshToken;
  }

  setCallbacks(callbacks: {
    onTokenRefresh?: (tokens: {
      accessToken: string;
      refreshToken: string;
    }) => void;
    onAuthError?: () => void;
    /** The account was deleted (M51), here or on another device. */
    onAccountDeleted?: () => void;
  }) {
    this.onTokenRefresh = callbacks.onTokenRefresh;
    this.onAuthError = callbacks.onAuthError;
    this.onAccountDeleted = callbacks.onAccountDeleted;
  }

  private async request<T>(
    endpoint: string,
    options: RequestOptions = {}
  ): Promise<T> {
    const { method = 'GET', body, headers = {}, timeoutMs, raw } = options;

    const requestHeaders: Record<string, string> = {
      // Only send Content-Type when there IS a body — Fastify 400s on an
      // empty JSON body otherwise (FST_ERR_CTP_EMPTY_JSON_BODY)
      ...(body ? { 'Content-Type': 'application/json' } : {}),
      ...headers,
    };

    if (this.accessToken) {
      requestHeaders['Authorization'] = `Bearer ${this.accessToken}`;
    }

    let response: Response;
    try {
      response = await fetch(`${API_BASE}${endpoint}`, {
        method,
        headers: requestHeaders,
        body: body ? JSON.stringify(body) : undefined,
        signal: timeoutMs ? AbortSignal.timeout(timeoutMs) : undefined,
      });
    } catch {
      throw networkError();
    }

    // The account is gone (M51): nothing to refresh; this device forgets
    // its session, encryption key and push subscription.
    if (response.status === 401) {
      const code = await response
        .clone()
        .json()
        .then((body: { error?: { code?: string } }) => body.error?.code)
        .catch(() => undefined);
      if (code === 'ACCOUNT_DELETED') {
        this.onAccountDeleted?.();
        throw await toApiError(response);
      }
    }

    // Handle 401 - try to refresh token
    if (response.status === 401 && this.refreshToken) {
      const refreshed = await this.tryRefreshToken();
      if (refreshed) {
        // Retry the request with new token
        requestHeaders['Authorization'] = `Bearer ${this.accessToken}`;
        let retryResponse: Response;
        try {
          retryResponse = await fetch(`${API_BASE}${endpoint}`, {
            method,
            headers: requestHeaders,
            body: body ? JSON.stringify(body) : undefined,
          });
        } catch {
          throw networkError();
        }

        if (!retryResponse.ok) {
          throw await toApiError(retryResponse);
        }

        return this.finish<T>(retryResponse, raw);
      }
    }

    if (!response.ok) {
      throw await toApiError(response);
    }

    return this.finish<T>(response, raw);
  }

  // One refresh at a time (D214). A refresh rotates the session's token, so
  // two refreshes sent with the same token race: this tab shares one
  // in-flight refresh between all its requests, and tabs take turns through
  // a Web Lock, each re-reading the stored token first in case another tab
  // has just refreshed it. (The server also forgives a racing token for 30 s.)
  private refreshing: Promise<boolean> | null = null;

  private tryRefreshToken(): Promise<boolean> {
    if (!this.refreshToken) return Promise.resolve(false);
    if (!this.refreshing) {
      const used = this.refreshToken;
      const run = () => this.refreshWith(used);
      const locks =
        typeof navigator !== 'undefined' ? navigator.locks : undefined;
      const refreshing: Promise<boolean> = (
        locks?.request
          ? // A sandboxed or opaque origin can refuse the lock itself: refresh
            // without it, as a browser with no Web Locks does.
            (async (): Promise<boolean> => {
              try {
                return (await locks.request(REFRESH_LOCK, run)) as boolean;
              } catch {
                return run();
              }
            })()
          : run()
      ).finally(() => {
        this.refreshing = null;
      });
      this.refreshing = refreshing;
      return refreshing;
    }
    return this.refreshing;
  }

  private async refreshWith(used: string): Promise<boolean> {
    // Another tab refreshed while this one waited: use its tokens.
    const stored = readStoredToken('twinden:refreshToken');
    if (stored && stored !== used) {
      this.refreshToken = stored;
      this.accessToken =
        readStoredToken('twinden:accessToken') ?? this.accessToken;
      return true;
    }

    try {
      const response = await fetch(`${API_BASE}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken: used }),
      });

      if (!response.ok) {
        this.onAuthError?.();
        return false;
      }

      const data = await response.json();
      this.accessToken = data.data.tokens.accessToken;
      this.refreshToken = data.data.tokens.refreshToken;
      this.onTokenRefresh?.(data.data.tokens);
      return true;
    } catch {
      // Network failure — keep the session and let the caller surface a
      // connection error instead of silently logging the user out.
      return false;
    }
  }

  // Auth endpoints
  async logout() {
    return this.request<{ success: boolean }>('/auth/logout', {
      method: 'POST',
      body: { refreshToken: this.refreshToken },
    });
  }

  async getMe() {
    return this.request<{
      success: boolean;
      data: {
        id: string;
        kind: 'human' | 'app';
        handle: string;
        displayName: string;
        avatarUrl: string | null;
        publicKey: string | null;
        birthDay: number | null;
        birthMonth: number | null;
        lastSeenAt: string | null;
        createdAt: string;
        /** Signed up with the family link and not let in yet (D153). */
        awaitingFirstFamily?: boolean;
      };
    }>('/auth/me');
  }

  // User endpoints
  async getUsers(search?: string) {
    const params = search ? `?search=${encodeURIComponent(search)}` : '';
    return this.request<{
      success: boolean;
      data: Array<{
        id: string;
        handle: string;
        displayName: string;
        avatarUrl: string | null;
        lastSeenAt: string | null;
      }>;
    }>(`/users${params}`);
  }

  // Conversation endpoints (family-scoped, D4/D8)
  async getConversations(familyId: string) {
    return this.request<{
      success: boolean;
      data: Array<{
        id: string;
        familyId: string;
        type: string;
        name: string | null;
        avatarUrl: string | null;
        isDefault: boolean;
        lastMessageAt: string | null;
        unreadCount?: number;
        participants: Array<{
          id: string;
          handle: string;
          displayName: string;
          avatarUrl: string | null;
          lastSeenAt: string | null;
          role: 'admin' | 'member';
        }>;
        createdAt: string;
      }>;
    }>(`/conversations?familyId=${encodeURIComponent(familyId)}`);
  }

  async getConversation(id: string) {
    return this.request<{
      success: boolean;
      data: {
        id: string;
        familyId: string;
        type: string;
        name: string | null;
        avatarUrl: string | null;
        isDefault: boolean;
        lastMessageAt: string | null;
        unreadCount?: number;
        participants: Array<{
          id: string;
          handle: string;
          displayName: string;
          avatarUrl: string | null;
          lastSeenAt: string | null;
          publicKey: string | null;
          role: 'admin' | 'member';
        }>;
        createdAt: string;
      };
    }>(`/conversations/${id}`);
  }

  async createFamilyConversation(
    familyId: string,
    data:
      | { type: 'group'; name: string; participantIds: string[] }
      | { type: 'direct'; userId: string }
  ) {
    return this.request<{
      success: boolean;
      data: { id: string };
    }>(`/families/${familyId}/conversations`, {
      method: 'POST',
      body: data,
    });
  }

  /** Group info (M63, D234): name, sealed description, picture. */
  async updateConversation(
    id: string,
    data: {
      name?: string;
      description?: {
        content: string;
        keyEpoch: number;
        clientId: string;
        // M76: v2, bound to its revision (D325, D326).
        encryption?: EnvelopeVersion;
        revision?: number;
      } | null;
      /** Sealed with the chat's key (D291); null removes it. */
      picture?: {
        content: string;
        keyEpoch: number;
        clientId: string;
        conversationId?: string;
        encryption?: EnvelopeVersion;
        revision?: number;
      } | null;
    }
  ) {
    return this.request<{
      success: boolean;
      data: {
        id: string;
        name: string | null;
        avatarUrl: string | null;
        description: SealedDescription | null;
      };
    }>(`/conversations/${id}`, {
      method: 'PATCH',
      body: data,
    });
  }

  /** My own settings for a chat (M63, D231–D233). */
  // Disappearing messages for a chat (M64, D238).
  async setDisappearing(id: string, duration: 'off' | '24h' | '7d' | '90d') {
    return this.request<{
      success: boolean;
      data: { conversationId: string; disappearingSeconds: number | null };
    }>(`/conversations/${id}/disappearing`, {
      method: 'PUT',
      body: { duration },
    });
  }

  async updateChatSettings(
    id: string,
    data: {
      archived?: boolean;
      keepArchived?: boolean;
      markedUnread?: boolean;
      mute?: '8h' | '1w' | 'always' | null;
      pinned?: boolean;
    }
  ) {
    return this.request<{ success: boolean; data: ChatSettings }>(
      `/conversations/${id}/settings`,
      { method: 'PATCH', body: data }
    );
  }

  async deleteConversation(id: string) {
    return this.request<{ success: boolean; data: { deleted: boolean } }>(
      `/conversations/${id}`,
      { method: 'DELETE' }
    );
  }

  async addConversationParticipants(id: string, userIds: string[]) {
    return this.request<{ success: boolean; data: { added: string[] } }>(
      `/conversations/${id}/participants`,
      { method: 'POST', body: { userIds } }
    );
  }

  async removeConversationParticipant(id: string, userId: string) {
    return this.request<{ success: boolean; data: { removed: boolean } }>(
      `/conversations/${id}/participants/${userId}`,
      { method: 'DELETE' }
    );
  }

  async leaveConversation(id: string) {
    return this.request<{ success: boolean; data: { left: boolean } }>(
      `/conversations/${id}/leave`,
      { method: 'POST' }
    );
  }

  async setConversationParticipantRole(
    id: string,
    userId: string,
    role: 'admin' | 'member'
  ) {
    return this.request<{
      success: boolean;
      data: { userId: string; role: 'admin' | 'member' };
    }>(`/conversations/${id}/participants/${userId}`, {
      method: 'PATCH',
      body: { role },
    });
  }

  // Message endpoints
  async getMessages(conversationId: string, cursor?: string, limit?: number) {
    const query = new URLSearchParams();
    if (cursor) query.set('cursor', cursor);
    if (limit) query.set('limit', String(limit));
    const params = query.toString() ? `?${query.toString()}` : '';
    return this.request<{
      success: boolean;
      data: Array<{
        id: string;
        conversationId: string;
        sender: {
          id: string;
          handle: string;
          displayName: string;
          avatarUrl: string | null;
        };
        content: string;
        type: string;
        metadata: Record<string, unknown> | null;
        readBy: string[];
        createdAt: string;
      }>;
      pagination: {
        hasMore: boolean;
        cursor?: string;
      };
    }>(`/messages/conversation/${conversationId}${params}`);
  }

  async sendMessage(data: {
    conversationId: string;
    content: string;
    replyToId?: string;
    /** M23 (D32): lets a retry return the original instead of a copy. */
    clientId?: string;
    /** M28: content is an encrypted envelope under this chat key epoch. */
    encryption?: EnvelopeVersion;
    keyEpoch?: number;
    mentions?: string[];
    mentionsEveryone?: boolean;
    type?: string;
    metadata?: {
      fileName?: string;
      fileSize?: number;
      mimeType?: string;
      duration?: number;
      waveform?: number[];
      width?: number;
      height?: number;
      thumbnailUrl?: string;
      files?: string[];
      /** M60 (D219): an album's counts. */
      album?: { photos: number; videos: number };
      /** M60 (D220): a poll's settings. */
      poll?: {
        options: number;
        multiple: boolean;
        closesInSeconds: number | null;
      };
      /** M71 (D282): when a gathering starts and whether it may ring. */
      gathering?: { startsAt: string; rings: boolean };
    };
  }) {
    return this.request<{
      success: boolean;
      data: {
        id: string;
        conversationId: string;
        content: string;
        type: string;
        metadata: Record<string, unknown> | null;
        createdAt: string;
      };
    }>('/messages', {
      method: 'POST',
      body: data,
      timeoutMs: SEND_TIMEOUT_MS,
    });
  }

  // Multipart upload (§6.2). Kept separate from request() because the body is
  // FormData, but shares the token-refresh behaviour.
  // ---- Chat essentials (M20, docs/chat-essentials.md) ----

  // One message as it is now (M37 route): a draft's reply target is looked
  // up again rather than kept (M62, D228). Opened by the response transform.
  async getMessage(messageId: string) {
    return this.request<{
      success: boolean;
      data: { message: unknown; conversation: { id: string } };
    }>(`/messages/${messageId}`);
  }

  // Message info (M62, D227): sender only.
  async getMessageReceipts(messageId: string) {
    return this.request<{ success: boolean; data: MessageReceipts }>(
      `/messages/${messageId}/receipts`
    );
  }

  async getMessagesAfter(conversationId: string, after: string) {
    return this.request<{
      success: boolean;
      data: unknown[];
      pagination: { hasMore: boolean; cursor?: string; truncated: boolean };
    }>(
      `/messages/conversation/${conversationId}?after=${encodeURIComponent(after)}`
    );
  }

  async editMessage(
    messageId: string,
    content: string,
    sealed?: {
      keyEpoch: number;
      /** M76: 'v2' with the revision it was sealed for (D326). */
      encryption?: EnvelopeVersion;
      revision?: number;
      mentions?: string[];
      mentionsEveryone?: boolean;
    }
  ) {
    return this.request<{ success: boolean; data: unknown }>(
      `/messages/${messageId}`,
      {
        method: 'PATCH',
        body: sealed ? { content, encryption: 'v1', ...sealed } : { content },
      }
    );
  }

  async deleteMessage(messageId: string) {
    return this.request<{ success: boolean; data: { deleted: true } }>(
      `/messages/${messageId}`,
      { method: 'DELETE' }
    );
  }

  /** Sends a request the outbox built earlier (M29, D68). */
  async sendQueued<T = { success: boolean; data: unknown }>(
    request: QueuedRequest
  ) {
    return this.request<T>(request.path, {
      method: request.method,
      body: request.body,
      timeoutMs: SEND_TIMEOUT_MS,
    });
  }

  /** Same emoji as your current reaction removes it (D21). */
  async reactToMessage(messageId: string, emoji: string) {
    return this.request<{ success: boolean; data: { emoji: string | null } }>(
      `/messages/${messageId}/reaction`,
      { method: 'PUT', body: { emoji } }
    );
  }

  async searchMessages(conversationId: string, query: string) {
    return this.request<{
      success: boolean;
      data: Array<{
        id: string;
        content: string;
        createdAt: string;
        sender: { id: string; displayName: string } | null;
      }>;
    }>(
      `/messages/conversation/${conversationId}/search?q=${encodeURIComponent(query)}`
    );
  }

  // ---- Scheduled messages (M64, docs/chat-round-six.md, D239) ----

  /** Everything I have waiting, in every chat (for resealing on connect). */
  async getAllScheduled() {
    return this.request<{ success: boolean; data: ScheduledMessage[] }>(
      '/conversations/scheduled'
    );
  }

  async getScheduled(conversationId: string) {
    return this.request<{ success: boolean; data: ScheduledMessage[] }>(
      `/conversations/${conversationId}/scheduled`
    );
  }

  async scheduleMessage(
    conversationId: string,
    body: {
      clientId: string;
      content: string;
      encryption?: EnvelopeVersion;
      keyEpoch?: number;
      mentions?: string[];
      mentionsEveryone?: boolean;
      replyToId?: string;
      scheduledFor: string;
    }
  ) {
    return this.request<{ success: boolean; data: ScheduledMessage }>(
      `/conversations/${conversationId}/scheduled`,
      { method: 'POST', body }
    );
  }

  /** Sealed again under the current key; sent at once if it's due. */
  async resealScheduled(
    conversationId: string,
    scheduledId: string,
    body: { content: string; encryption?: EnvelopeVersion; keyEpoch?: number }
  ) {
    return this.request<{
      success: boolean;
      data: { scheduled: ScheduledMessage | null; message: unknown | null };
    }>(`/conversations/${conversationId}/scheduled/${scheduledId}`, {
      method: 'PATCH',
      body,
    });
  }

  async sendScheduledNow(conversationId: string, scheduledId: string) {
    return this.request<{
      success: boolean;
      data: { scheduled: null; message: unknown | null };
    }>(`/conversations/${conversationId}/scheduled/${scheduledId}/send`, {
      method: 'POST',
    });
  }

  async cancelScheduled(conversationId: string, scheduledId: string) {
    return this.request<{ success: boolean; data: { cancelled: boolean } }>(
      `/conversations/${conversationId}/scheduled/${scheduledId}`,
      { method: 'DELETE' }
    );
  }

  // ---- Chat round two (M22, docs/chat-round-two.md) ----

  async getPins(conversationId: string) {
    return this.request<{ success: boolean; data: unknown[] }>(
      `/messages/conversation/${conversationId}/pins`
    );
  }

  /** Returns the conversation's pins after the change (D28). */
  async setPinned(messageId: string, pinned: boolean) {
    return this.request<{ success: boolean; data: unknown[] }>(
      `/messages/${messageId}/pin`,
      { method: pinned ? 'PUT' : 'DELETE' }
    );
  }

  async forwardMessage(
    messageId: string,
    conversationIds: string[],
    encrypted?: Array<{
      conversationId: string;
      content: string;
      keyEpoch: number;
      clientId: string;
      encryption?: EnvelopeVersion;
    }>
  ) {
    return this.request<{ success: boolean; data: unknown[] }>(
      `/messages/${messageId}/forward`,
      {
        method: 'POST',
        body: encrypted?.length
          ? { conversationIds, encrypted }
          : { conversationIds },
      }
    );
  }

  // ---- Polls (M60, D220) ----

  /** Replaces your votes; returns the poll with `myVotes`. */
  async votePoll(messageId: string, optionIndexes: number[]) {
    return this.request<{ success: boolean; data: { poll: PollState } }>(
      `/messages/${messageId}/poll/vote`,
      { method: 'POST', body: { optionIndexes } }
    );
  }

  async retractPollVote(messageId: string) {
    return this.request<{ success: boolean; data: { poll: PollState } }>(
      `/messages/${messageId}/poll/vote`,
      { method: 'DELETE' }
    );
  }

  /** The sender closes it early. */
  async closePoll(messageId: string) {
    return this.request<{ success: boolean; data: { poll: PollState } }>(
      `/messages/${messageId}/poll/close`,
      { method: 'POST' }
    );
  }

  // ---- Gatherings (M71, docs/gatherings.md) ----
  // Each answers with the gathering as it now stands. Errors carry codes
  // (GATHERING_ENDED, GATHERING_LIMIT, …) for the card to word itself.

  /** Coming, Late (+15/+30/+60) or Not today; replaces your answer (D284). */
  async replyToGathering(
    messageId: string,
    body: { reply: GatheringReply; lateMinutes?: number }
  ): Promise<GatheringState> {
    return this.gatheringRequest(messageId, 'reply', 'POST', body);
  }

  /** Takes your answer back, arrival included (D284). */
  async takeBackGatheringReply(messageId: string): Promise<GatheringState> {
    return this.gatheringRequest(messageId, 'reply', 'DELETE');
  }

  /** "I'm here" (D287): stops the rings for you. */
  async arriveAtGathering(messageId: string): Promise<GatheringState> {
    return this.gatheringRequest(messageId, 'arrived', 'POST');
  }

  /** The organiser cancels it (D288). */
  async cancelGathering(messageId: string): Promise<GatheringState> {
    return this.gatheringRequest(messageId, 'cancel', 'POST');
  }

  /** "Come now" (D286): at most twice, 3 minutes apart. */
  async ringGathering(messageId: string): Promise<GatheringState> {
    return this.gatheringRequest(messageId, 'ring', 'POST');
  }

  private async gatheringRequest(
    messageId: string,
    action: 'reply' | 'arrived' | 'cancel' | 'ring',
    method: 'POST' | 'DELETE',
    body?: object
  ): Promise<GatheringState> {
    const response = await this.request<{
      success: boolean;
      data: { gathering: GatheringState };
    }>(`/messages/${messageId}/gathering/${action}`, {
      method,
      ...(body ? { body } : {}),
    });
    return response.data.gathering;
  }

  /** Delete for me (D30). */
  async hideMessage(messageId: string) {
    return this.request<{ success: boolean; data: { hidden: true } }>(
      `/messages/${messageId}/hide`,
      { method: 'POST' }
    );
  }

  // Multipart upload with progress (issue #2). XHR rather than fetch: fetch
  // reports no upload progress, and a stalled mobile upload must fail loudly
  // instead of leaving the user watching a spinner forever.
  async uploadAttachment(
    file: Blob,
    fileName: string,
    meta: { width?: number; height?: number; duration?: number } = {},
    thumbnail?: Blob,
    options: UploadOptions = {}
  ): Promise<UploadedAttachment> {
    const buildForm = () => {
      const form = new FormData();
      if (meta.width !== undefined) form.append('width', String(meta.width));
      if (meta.height !== undefined) form.append('height', String(meta.height));
      if (meta.duration !== undefined) {
        form.append('duration', String(meta.duration));
      }
      if (options.scope?.familyId) {
        form.append('familyId', options.scope.familyId);
      }
      if (options.scope?.conversationId) {
        form.append('conversationId', options.scope.conversationId);
      }
      // Fields before files: the server reads parts in order.
      form.append('file', file, fileName);
      if (thumbnail) form.append('thumbnail', thumbnail, 'thumbnail.jpg');
      return form;
    };

    let result = await this.sendUpload(buildForm(), options);
    if (result.status === 401 && this.refreshToken) {
      if (await this.tryRefreshToken()) {
        result = await this.sendUpload(buildForm(), options);
      }
    }

    let body: {
      success?: boolean;
      data?: UploadedAttachment;
      error?: { code?: string; message?: string };
    } = {};
    try {
      body = JSON.parse(result.body);
    } catch {
      // Proxies answer some failures (e.g. 413) with HTML.
    }

    if (result.status < 200 || result.status >= 300 || !body.data) {
      if (isUpdateRequired(result.status)) {
        markUpdateRequired(body.error?.message);
      }
      if (result.status === 413) {
        throw new ApiError(
          'This file is too large to send.',
          'FILE_TOO_LARGE',
          413
        );
      }
      throw new ApiError(
        body.error?.message || 'Upload failed',
        body.error?.code || 'UPLOAD_FAILED',
        result.status
      );
    }
    return body.data;
  }

  private sendUpload(
    form: FormData,
    { onProgress, signal }: UploadOptions
  ): Promise<{ status: number; body: string }> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      let stallTimer: ReturnType<typeof setTimeout> | undefined;

      const fail = (error: ApiError) => {
        clearTimeout(stallTimer);
        signal?.removeEventListener('abort', onAbort);
        reject(error);
      };
      // Reset on every progress event: slow is fine, stuck is not.
      const armStallTimer = (ms = UPLOAD_STALL_MS) => {
        clearTimeout(stallTimer);
        stallTimer = setTimeout(() => {
          xhr.abort();
          fail(
            new ApiError(
              'The upload stopped making progress. Check your connection and try again.',
              'UPLOAD_STALLED',
              0
            )
          );
        }, ms);
      };
      const onAbort = () => {
        xhr.abort();
        fail(new ApiError('Upload cancelled', 'UPLOAD_CANCELLED', 0));
      };

      if (signal?.aborted) {
        onAbort();
        return;
      }
      signal?.addEventListener('abort', onAbort);

      xhr.open('POST', `${API_BASE}/uploads`);
      if (this.accessToken) {
        xhr.setRequestHeader('Authorization', `Bearer ${this.accessToken}`);
      }
      xhr.upload.onprogress = (event) => {
        armStallTimer();
        if (event.lengthComputable && event.total > 0) {
          onProgress?.(event.loaded / event.total);
        }
      };
      // Bytes are all sent; the server is now copying the file to storage,
      // which for a large video can take a while.
      xhr.upload.onload = () => armStallTimer(UPLOAD_STORE_MS);
      xhr.onload = () => {
        clearTimeout(stallTimer);
        signal?.removeEventListener('abort', onAbort);
        resolve({ status: xhr.status, body: xhr.responseText });
      };
      xhr.onerror = () => fail(networkError());

      armStallTimer();
      xhr.send(form);
    });
  }

  async markMessagesRead(conversationId: string, messageIds: string[]) {
    return this.request<{
      success: boolean;
      data: { markedRead: number };
    }>('/messages/read', {
      method: 'POST',
      body: { conversationId, messageIds },
    });
  }

  // ---- Stars (family-apps spec §3, D14) ----

  async starMessage(messageId: string) {
    return this.request<{ success: boolean; data: { starred: boolean } }>(
      `/messages/${messageId}/star`,
      { method: 'POST' }
    );
  }

  async unstarMessage(messageId: string) {
    return this.request<{ success: boolean; data: { starred: boolean } }>(
      `/messages/${messageId}/unstar`,
      { method: 'DELETE' }
    );
  }

  async getStarred(familyId: string, cursor?: string) {
    const params = new URLSearchParams();
    if (cursor) params.set('cursor', cursor);
    const suffix = params.toString() ? `?${params.toString()}` : '';
    return this.request<{
      success: boolean;
      data: StarredMessage[];
      pagination: { hasMore: boolean; cursor?: string };
    }>(`/families/${familyId}/starred${suffix}`);
  }

  // ---- Mentions (M25, docs/search-and-mentions.md) ----

  async getMentions(familyId: string, cursor?: string) {
    const params = new URLSearchParams();
    if (cursor) params.set('cursor', cursor);
    const suffix = params.toString() ? `?${params.toString()}` : '';
    return this.request<{
      success: boolean;
      data: MentionItem[];
      pagination: { hasMore: boolean; cursor?: string };
    }>(`/families/${familyId}/mentions${suffix}`);
  }

  // ---- Report and block (M58, D207, D208) ----

  async createReport(familyId: string, data: ReportInput) {
    return this.request<{
      success: boolean;
      data: { report: { id: string; createdAt: string }; blocked: boolean };
    }>(`/families/${familyId}/reports`, {
      method: 'POST',
      body: data,
    });
  }

  async getFamilyReports(familyId: string) {
    return this.request<{
      success: boolean;
      data: { reports: FamilyReport[] };
    }>(`/families/${familyId}/reports`);
  }

  async markFamilyReport(familyId: string, id: string, handled: boolean) {
    return this.request<{ success: boolean; data: { report: unknown } }>(
      `/families/${familyId}/reports/${id}`,
      { method: 'PATCH', body: { handled } }
    );
  }

  async getBlocks() {
    return this.request<{
      success: boolean;
      data: { blocks: BlockedPerson[] };
    }>('/users/me/blocks');
  }

  async blockUser(userId: string) {
    return this.request<{ success: boolean; data: { blocked: boolean } }>(
      '/users/me/blocks',
      { method: 'POST', body: { userId } }
    );
  }

  async unblockUser(userId: string) {
    return this.request<{ success: boolean; data: { blocked: boolean } }>(
      `/users/me/blocks/${userId}`,
      { method: 'DELETE' }
    );
  }

  async getUnreadMentions(familyId: string) {
    return this.request<{ success: boolean; data: { count: number } }>(
      `/families/${familyId}/mentions/unread`
    );
  }

  async getCallHistory(familyId: string, cursor?: string) {
    const params = new URLSearchParams();
    if (cursor) params.set('cursor', cursor);
    const suffix = params.toString() ? `?${params.toString()}` : '';
    return this.request<{
      success: boolean;
      data: CallHistoryEntry[];
      pagination: { hasMore: boolean; cursor?: string };
    }>(`/families/${familyId}/calls${suffix}`);
  }

  // ---- Link previews (family-apps spec §4.1) ----

  // The link goes in a POST body, never the URL, so it stays out of the
  // server's request log (#80, D61).
  // `image`: also the page's picture, as bytes to shrink and seal (D318).
  async getLinkPreview(
    url: string,
    options: { ephemeral?: boolean; image?: boolean } = {}
  ) {
    return this.request<{ success: boolean; data: LinkPreview }>(
      '/link-preview',
      {
        method: 'POST',
        body: {
          url,
          ...(options.ephemeral ? { ephemeral: true } : {}),
          ...(options.image ? { image: true } : {}),
        },
      }
    );
  }

  // ---- End-to-end encryption keys (M28) ----

  async registerDeviceKey(
    publicKey: string,
    label?: string,
    /** M78 (D336): the device's ML-KEM-768 key, published with it. */
    mlkemPublicKey?: string
  ) {
    return this.request<{ success: boolean; data: { id: string } }>(
      '/keys/devices',
      {
        method: 'POST',
        body: {
          publicKey,
          ...(label ? { label } : {}),
          ...(mlkemPublicKey ? { mlkemPublicKey } : {}),
        },
      }
    );
  }

  /**
   * A device registered before M78 publishes its ML-KEM key, once (D336).
   * 409 MLKEM_KEY_SET when the server has another one for it.
   */
  async setMlkemKey(id: string, mlkemPublicKey: string) {
    return this.request<{
      success: boolean;
      data: {
        id: string;
        mlkemPublicKey: string;
        /** The highest epoch this device holds per scope (review M4). */
        held?: Array<{
          scope: 'chat' | 'family';
          scopeId: string;
          epoch: number;
        }>;
      };
    }>(`/keys/devices/${encodeURIComponent(id)}/mlkem`, {
      method: 'PUT',
      body: { mlkemPublicKey },
    });
  }

  /** The highest epoch this device holds per scope (M78, review M4). */
  async getHeldKeys(id: string) {
    return this.request<{
      success: boolean;
      data: {
        held: Array<{
          scope: 'chat' | 'family';
          scopeId: string;
          epoch: number;
        }>;
      };
    }>(`/keys/devices/${encodeURIComponent(id)}/held`);
  }

  async deleteDeviceKey(id: string) {
    return this.request<{ success: boolean }>(`/keys/devices/${id}`, {
      method: 'DELETE',
    });
  }

  /** Asks family devices again for keys this device still lacks (D57). */
  async askForKeys(deviceId: string) {
    return this.request<{ success: boolean; data: { asked: boolean } }>(
      `/keys/devices/${encodeURIComponent(deviceId)}/needed`,
      { method: 'POST' }
    );
  }

  /** Keys for a chat ('chat') or a family's apps ('family'), M28. */
  /** Everyone's wrapped picture key for one family (D292). */
  async getPictureKeys(familyId: string) {
    return this.request<{
      success: boolean;
      data: Array<{ userId: string; pictureHash: string; sealed: string }>;
    }>(`/families/${familyId}/picture-keys`);
  }

  /**
   * Wraps my current picture's key for one family, after joining it or a
   * rotation (D295). 409 STALE_PICTURE when that picture isn't current.
   */
  async putPictureKey(
    familyId: string,
    data: { pictureHash: string; sealed: string }
  ) {
    return this.request<{ success: boolean; data: { pictureHash: string } }>(
      `/families/${familyId}/picture-keys/me`,
      { method: 'PUT', body: data }
    );
  }

  async getScopeKeys(scope: 'chat' | 'family', id: string, deviceId: string) {
    return this.request<{
      success: boolean;
      data: {
        currentEpoch: number;
        needsRotation: boolean;
        encryptedSince: string | null;
        /** M76 (D328): where v2 began in this scope, or null. */
        v2SinceEpoch?: number | null;
        /** When each epoch began (M73); absent from older servers. */
        epochs?: Array<{ epoch: number; createdAt: string }>;
        devices: Array<{
          id: string;
          userId: string;
          publicKey: string;
          /** M78 (D336): null for an older app; absent from older servers. */
          mlkemPublicKey?: string | null;
        }>;
        keys: Array<{
          epoch: number;
          wrappedKey: string;
          wrappedByPublicKey: string;
        }>;
        /** M78 (§6.5): devices without an ML-KEM key get no new keys. */
        pqGraceOver?: boolean;
      };
    }>(
      `/keys/${scope === 'chat' ? 'conversations' : 'families'}/${id}?deviceId=${encodeURIComponent(deviceId)}`
    );
  }

  async createKeyEpoch(
    scope: 'chat' | 'family',
    id: string,
    body: {
      epoch: number;
      wrappedByDeviceId: string;
      keys: Array<{ deviceKeyId: string; wrappedKey: string }>;
    }
  ) {
    return this.request<{ success: boolean; data: { epoch: number } }>(
      `/keys/${scope === 'chat' ? 'conversations' : 'families'}/${id}/epochs`,
      { method: 'POST', body }
    );
  }

  async shareKeys(
    scope: 'chat' | 'family',
    id: string,
    body: {
      epoch: number;
      wrappedByDeviceId: string;
      keys: Array<{ deviceKeyId: string; wrappedKey: string }>;
    }
  ) {
    return this.request<{ success: boolean; data: { shared: number } }>(
      `/keys/${scope === 'chat' ? 'conversations' : 'families'}/${id}/shares`,
      { method: 'POST', body }
    );
  }

  /**
   * Keys this device holds that others lack (D57), each device's two keys
   * once beside rows of ids (M78, §9).
   */
  async getPendingKeyShares(deviceId: string): Promise<{
    success: boolean;
    data: PendingKeyShare[];
    /** M78 (§6.5): devices without an ML-KEM key get no new keys. */
    pqGraceOver?: boolean;
  }> {
    const response = await this.request<{
      success: boolean;
      data:
        | {
            rows: Array<{
              scope: 'chat' | 'family';
              scopeId: string;
              epoch: number;
              deviceKeyId: string;
            }>;
            devices: Record<
              string,
              {
                userId: string;
                publicKey: string;
                mlkemPublicKey: string | null;
              }
            >;
            pqGraceOver: boolean;
          }
        // A server from before M78 (a rollback) answers the older shape.
        | PendingKeyShare[];
    }>(`/keys/pending?deviceId=${encodeURIComponent(deviceId)}&v=2`);
    const data = response.data;
    if (Array.isArray(data)) return { success: response.success, data };
    return {
      success: response.success,
      data: data.rows.flatMap((row) => {
        const device = data.devices[row.deviceKeyId];
        return device
          ? [
              {
                ...row,
                userId: device.userId,
                publicKey: device.publicKey,
                mlkemPublicKey: device.mlkemPublicKey,
              },
            ]
          : [];
      }),
      pqGraceOver: data.pqGraceOver === true,
    };
  }

  /**
   * Who is in a chat or family, as the app shows it, for the device-trust
   * check (M73, D301): ids only, read without opening anything (a sealed
   * description would wait on the very keys being checked).
   */
  async getScopeMemberIds(scope: 'chat' | 'family', id: string) {
    if (scope === 'chat') {
      const response = await this.request<{
        data: { participants: Array<{ id: string }> };
      }>(`/conversations/${id}`, { raw: true });
      return response.data.participants.map((p) => p.id);
    }
    const response = await this.request<{
      data: { members: Array<{ userId: string }> };
    }>(`/families/${id}`, { raw: true });
    return response.data.members.map((m) => m.userId);
  }

  /**
   * A family's chats with their participants' ids, read without opening
   * anything (M73, D304): the first-look lists on a device from before
   * device trust.
   */
  async getChatMemberLists(familyId: string) {
    const response = await this.request<{
      data: Array<{ id: string; participants: Array<{ id: string }> }>;
    }>(`/conversations?familyId=${encodeURIComponent(familyId)}`, {
      raw: true,
    });
    return response.data.map((chat) => ({
      id: chat.id,
      userIds: chat.participants.map((p) => p.id),
    }));
  }

  async getUserDeviceKeys(userId: string) {
    return this.request<{
      success: boolean;
      data: {
        devices: Array<{
          id: string;
          publicKey: string;
          /** M78 (D336): absent from older servers. */
          mlkemPublicKey?: string | null;
          /** After the grace: a device without an ML-KEM key (§6.5). */
          needsUpdate?: boolean;
        }>;
        pqGraceOver?: boolean;
      };
    }>(`/keys/users/${userId}`);
  }

  // ---- Family Links (family-apps spec §4.2) ----

  async getFamilyLinks(
    familyId: string,
    options: { q?: string; cursor?: string } = {}
  ) {
    const params = new URLSearchParams();
    if (options.q) params.set('q', options.q);
    if (options.cursor) params.set('cursor', options.cursor);
    const suffix = params.toString() ? `?${params.toString()}` : '';
    return this.request<{
      success: boolean;
      data: FamilyLink[];
      pagination: { hasMore: boolean; cursor?: string };
    }>(`/families/${familyId}/links${suffix}`);
  }

  async addFamilyLink(
    familyId: string,
    input:
      | { url: string; note?: string }
      | { messageId: string; note?: string }
      | { sealed: string; messageId?: string; id?: string }
  ) {
    return this.request<{ success: boolean; data: FamilyLink }>(
      `/families/${familyId}/links`,
      { method: 'POST', body: input }
    );
  }

  async deleteFamilyLink(familyId: string, linkId: string) {
    return this.request<{ success: boolean; data: { deleted: boolean } }>(
      `/families/${familyId}/links/${linkId}`,
      { method: 'DELETE' }
    );
  }

  // ---- Family Documents (family-apps spec §5) ----

  async getFamilyDocuments(
    familyId: string,
    options: { q?: string; cursor?: string; folder?: string } = {}
  ) {
    const params = new URLSearchParams();
    if (options.q) params.set('q', options.q);
    if (options.folder) params.set('folder', options.folder);
    if (options.cursor) params.set('cursor', options.cursor);
    const suffix = params.toString() ? `?${params.toString()}` : '';
    return this.request<{
      success: boolean;
      data: FamilyDocument[];
      pagination: { hasMore: boolean; cursor?: string };
    }>(`/families/${familyId}/documents${suffix}`);
  }

  async addFamilyDocument(
    familyId: string,
    input:
      | { attachment: UploadedAttachment; description?: string }
      | { messageId: string; description?: string }
      | {
          sealed: string;
          attachmentUrl?: string;
          messageId?: string;
          id?: string;
        }
  ) {
    return this.request<{ success: boolean; data: FamilyDocument }>(
      `/families/${familyId}/documents`,
      { method: 'POST', body: input }
    );
  }

  // Folder, tags and note (M26, D49).
  async updateFamilyDocument(
    familyId: string,
    documentId: string,
    input:
      | {
          description?: string | null;
          folder?: string | null;
          tags?: string[];
        }
      | { sealed: string }
  ) {
    return this.request<{ success: boolean; data: FamilyDocument }>(
      `/families/${familyId}/documents/${documentId}`,
      { method: 'PATCH', body: input }
    );
  }

  async getDocumentFolders(familyId: string) {
    return this.request<{
      success: boolean;
      data: Array<{ folder: string; count: number }>;
    }>(`/families/${familyId}/documents/folders`);
  }

  async deleteFamilyDocument(familyId: string, documentId: string) {
    return this.request<{ success: boolean; data: { deleted: boolean } }>(
      `/families/${familyId}/documents/${documentId}`,
      { method: 'DELETE' }
    );
  }

  // ---- Family Lists (family-apps spec §6) ----

  async getFamilyLists(familyId: string) {
    return this.request<{ success: boolean; data: FamilyList[] }>(
      `/families/${familyId}/lists`
    );
  }

  async createFamilyList(
    familyId: string,
    // M76 (D329): a sealed list carries its id, and a template's items
    // sealed again for the new list.
    name:
      | string
      | {
          sealed: string;
          id?: string;
          items?: Array<{ id: string; sealed: string }>;
        },
    templateId?: string
  ) {
    const body = typeof name === 'string' ? { name } : name;
    return this.request<{ success: boolean; data: FamilyList }>(
      `/families/${familyId}/lists`,
      { method: 'POST', body: templateId ? { ...body, templateId } : body }
    );
  }

  async getListTemplates(familyId: string) {
    return this.request<{ success: boolean; data: ListTemplate[] }>(
      `/families/${familyId}/list-templates`
    );
  }

  async saveListTemplate(
    familyId: string,
    listId: string,
    // M76 (D329): sealed, with the template's own id and its items sealed
    // again for ids of their own.
    body: {
      name?: string;
      sealed?: string;
      id?: string;
      items?: Array<{ id: string; sealed: string }>;
    } = {}
  ) {
    return this.request<{ success: boolean; data: ListTemplate }>(
      `/families/${familyId}/lists/${listId}/template`,
      { method: 'POST', body }
    );
  }

  async deleteListTemplate(familyId: string, templateId: string) {
    return this.request<{ success: boolean }>(
      `/families/${familyId}/list-templates/${templateId}`,
      { method: 'DELETE' }
    );
  }

  async renameFamilyList(
    familyId: string,
    listId: string,
    name: string | { sealed: string }
  ) {
    return this.request<{ success: boolean; data: FamilyList }>(
      `/families/${familyId}/lists/${listId}`,
      { method: 'PATCH', body: typeof name === 'string' ? { name } : name }
    );
  }

  async deleteFamilyList(familyId: string, listId: string) {
    return this.request<{ success: boolean; data: { deleted: boolean } }>(
      `/families/${familyId}/lists/${listId}`,
      { method: 'DELETE' }
    );
  }

  async getFamilyList(familyId: string, listId: string) {
    return this.request<{ success: boolean; data: FamilyListDetail }>(
      `/families/${familyId}/lists/${listId}`
    );
  }

  async addFamilyListItem(
    familyId: string,
    listId: string,
    input:
      | { text: string; note?: string }
      | {
          sealed: string;
          id?: string;
          assignedTo?: string;
          dueDate?: string;
          repeat?: 'daily' | 'weekly' | 'monthly';
        }
  ) {
    return this.request<{
      success: boolean;
      data: { item: FamilyListItem; counts: ListCounts };
    }>(`/families/${familyId}/lists/${listId}/items`, {
      method: 'POST',
      body: input,
    });
  }

  async updateFamilyListItem(
    familyId: string,
    listId: string,
    itemId: string,
    input: {
      text?: string;
      note?: string | null;
      checked?: boolean;
      position?: number;
      assignedTo?: string | null;
      dueDate?: string | null;
      repeat?: 'daily' | 'weekly' | 'monthly' | null;
      /** M28 part 2: new text and note, encrypted. */
      sealed?: string;
    }
  ) {
    return this.request<{
      success: boolean;
      data: { item: FamilyListItem; counts: ListCounts };
    }>(`/families/${familyId}/lists/${listId}/items/${itemId}`, {
      method: 'PATCH',
      body: input,
    });
  }

  async deleteFamilyListItem(familyId: string, listId: string, itemId: string) {
    return this.request<{
      success: boolean;
      data: { deleted: boolean; counts: ListCounts };
    }>(`/families/${familyId}/lists/${listId}/items/${itemId}`, {
      method: 'DELETE',
    });
  }

  async clearCheckedFamilyListItems(familyId: string, listId: string) {
    return this.request<{
      success: boolean;
      data: { cleared: number; counts: ListCounts };
    }>(`/families/${familyId}/lists/${listId}/items/clear-checked`, {
      method: 'POST',
    });
  }

  // Family endpoints (identity spec §7.3)
  async getFamilies() {
    return this.request<{ success: boolean; data: FamilyListItem[] }>(
      '/families'
    );
  }

  /** What you call each member of a family (D143): member id → label. */
  async getMemberLabels(familyId: string) {
    return this.request<{ success: boolean; data: Record<string, string> }>(
      `/families/${familyId}/labels`
    );
  }

  async setMemberLabel(familyId: string, memberId: string, label: string) {
    return this.request<{ success: boolean; data: { label: string | null } }>(
      `/families/${familyId}/labels/${memberId}`,
      { method: 'PUT', body: { label } }
    );
  }

  async getFamily(id: string) {
    return this.request<{ success: boolean; data: FamilyDetail }>(
      `/families/${id}`
    );
  }

  async updateFamily(
    id: string,
    data: {
      name?: string;
      description?: string | null;
      /** A sealed `picture` record (D291); null removes it. */
      pictureSealed?: string | null;
    }
  ) {
    return this.request<{
      success: boolean;
      data: {
        id: string;
        name: string;
        handle: string;
        description: string | null;
        avatarUrl: string | null;
        updatedAt: string;
      };
    }>(`/families/${id}`, {
      method: 'PATCH',
      body: data,
    });
  }

  /** The live check while a root types a new family handle (D216). */
  async checkFamilyHandle(handle: string) {
    return this.request<{
      success: boolean;
      data: {
        available: boolean;
        reason?: 'invalid' | 'reserved' | 'taken';
      };
    }>(`/families/handles/available?${new URLSearchParams({ handle })}`);
  }

  /** Roots change the family handle, 3 times in 30 days (D216). */
  async setFamilyHandle(id: string, handle: string) {
    return this.request<{
      success: boolean;
      data: { id: string; handle: string; handleChangesLeft: number };
    }>(`/families/${id}/handle`, { method: 'PUT', body: { handle } });
  }

  // ---- Personal links for new people (D151; never a device, D334) ----

  async getFamilyEnrollmentTokens(familyId: string) {
    return this.request<{ success: boolean; data: EnrollmentTokenRow[] }>(
      `/families/${familyId}/enrollment-tokens`
    );
  }

  async createFamilyEnrollmentToken(
    familyId: string,
    data: {
      kind: 'enroll';
      // No handle: the new person picks theirs on the link page (D348).
      displayName: string;
      familyRole?: FamilyRole;
      expiresInDays?: number;
    }
  ) {
    return this.request<{ success: boolean; data: CreatedEnrollmentToken }>(
      `/families/${familyId}/enrollment-tokens`,
      { method: 'POST', body: data }
    );
  }

  // ---- Fun handles (D147) ----

  async getHandleSuggestions(
    familyId: string,
    name: string,
    count = 5,
    gendered = false
  ) {
    const query = new URLSearchParams({
      name,
      count: String(count),
      ...(gendered ? { gendered: '1' } : {}),
    });
    return this.request<{
      success: boolean;
      data: { suggestions: HandleSuggestion[] };
    }>(`/families/${familyId}/handle-suggestions?${query}`);
  }

  // ---- The family link (D152) ----

  async getJoinLink(familyId: string) {
    return this.request<{ success: boolean; data: JoinLink }>(
      `/families/${familyId}/join-link`
    );
  }

  async resetJoinLink(familyId: string) {
    return this.request<{ success: boolean; data: JoinLink }>(
      `/families/${familyId}/join-link/reset`,
      { method: 'POST' }
    );
  }

  async setJoinLinkEnabled(familyId: string, enabled: boolean) {
    return this.request<{ success: boolean; data: JoinLink }>(
      `/families/${familyId}/join-link`,
      { method: 'PATCH', body: { enabled } }
    );
  }

  // ---- People asking to join (D154) ----

  async getJoinRequests(familyId: string) {
    return this.request<{
      success: boolean;
      data: { requests: JoinRequest[] };
    }>(`/families/${familyId}/join-requests`);
  }

  async approveJoinRequest(familyId: string, id: string, role?: FamilyRole) {
    return this.request<{
      success: boolean;
      data: { request: { id: string; status: 'approved'; role: FamilyRole } };
    }>(`/families/${familyId}/join-requests/${id}/approve`, {
      method: 'POST',
      body: role ? { role } : {},
    });
  }

  async declineJoinRequest(familyId: string, id: string) {
    return this.request<{
      success: boolean;
      data: { request: { id: string; status: 'declined'; role: null } };
    }>(`/families/${familyId}/join-requests/${id}/decline`, {
      method: 'POST',
    });
  }

  async approveAllJoinRequests(familyId: string) {
    return this.request<{ success: boolean; data: { approved: number } }>(
      `/families/${familyId}/join-requests/approve-all`,
      { method: 'POST' }
    );
  }

  // ---- Joining with a link (D151, D153) ----

  /** Public: the family behind a family link, and who shared it. */
  async getJoinLinkInfo(secret: string, via?: string) {
    const query = via ? `?${new URLSearchParams({ via })}` : '';
    return this.request<{ success: boolean; data: JoinLinkInfo }>(
      `/join-link/${secret}${query}`
    );
  }

  async getJoinLinkSuggestions(secret: string, name: string, gendered = false) {
    const query = new URLSearchParams({
      name,
      ...(gendered ? { gendered: '1' } : {}),
    });
    return this.request<{
      success: boolean;
      data: { suggestions: HandleSuggestion[] };
    }>(`/join-link/${secret}/handle-suggestions?${query}`);
  }

  async joinLinkPasskeyStart(
    secret: string,
    body: { displayName: string; handle: string; handleToken: string }
  ) {
    return this.request<{
      success: boolean;
      data: { options: Record<string, unknown>; pendingUserId: string };
    }>(`/join-link/${secret}/passkey/start`, { method: 'POST', body });
  }

  async joinLinkPasskeyFinish(secret: string, body: JoinNewAccountBody) {
    return this.request<{
      success: boolean;
      data: AuthResponse & { request: MyJoinRequest };
    }>(`/join-link/${secret}/passkey/finish`, { method: 'POST', body });
  }

  /** Signed in: ask to join with the family link. */
  async askToJoin(secret: string, body: { note?: string; via?: string }) {
    return this.request<{
      success: boolean;
      data: { request: MyJoinRequest };
    }>(`/join-link/${secret}/requests`, { method: 'POST', body });
  }

  async getMyJoinRequests() {
    return this.request<{
      success: boolean;
      data: { requests: MyJoinRequest[] };
    }>('/join-requests/mine');
  }

  async cancelJoinRequest(id: string) {
    return this.request<{
      success: boolean;
      data: { request: { id: string; status: 'cancelled' } };
    }>(`/join-requests/${id}`, { method: 'DELETE' });
  }

  /** Signed in: a personal link adds me straight away (D151). */
  async joinWithPersonalLink(token: string) {
    return this.request<{ success: boolean; data: { familyId: string } }>(
      `/enroll/${token}/join`,
      { method: 'POST' }
    );
  }

  async revokeFamilyEnrollmentToken(familyId: string, tokenId: string) {
    return this.request<{ success: boolean; data: EnrollmentTokenRow }>(
      `/families/${familyId}/enrollment-tokens/${tokenId}/revoke`,
      { method: 'POST' }
    );
  }

  async regenerateFamilyEnrollmentToken(familyId: string, tokenId: string) {
    return this.request<{ success: boolean; data: CreatedEnrollmentToken }>(
      `/families/${familyId}/enrollment-tokens/${tokenId}/regenerate`,
      { method: 'POST' }
    );
  }

  // ---- Add someone you know (D350): invited, they join on Join ----

  /** People from my other families I can invite here (roots, admins). */
  async getPeopleYouKnow(familyId: string) {
    return this.request<{
      success: boolean;
      data: { people: PersonYouKnow[] };
    }>(`/families/${familyId}/people-you-know`);
  }

  async createFamilyInvite(
    familyId: string,
    data: { userId: string; familyRole?: FamilyRole }
  ) {
    return this.request<{ success: boolean; data: FamilyInviteRow }>(
      `/families/${familyId}/invites`,
      { method: 'POST', body: data }
    );
  }

  async getFamilyAuditLog(familyId: string, limit = 100) {
    return this.request<{ success: boolean; data: AuditEntry[] }>(
      `/families/${familyId}/audit-log?limit=${limit}`
    );
  }

  async getFamilyInvites(familyId: string) {
    return this.request<{ success: boolean; data: FamilyInviteRow[] }>(
      `/families/${familyId}/invites`
    );
  }

  async updateFamilyMember(
    familyId: string,
    userId: string,
    data: { role?: FamilyRole; nickname?: string | null }
  ) {
    return this.request<{
      success: boolean;
      data: {
        id: string;
        userId: string;
        role: FamilyRole;
        nickname: string | null;
      };
    }>(`/families/${familyId}/members/${userId}`, {
      method: 'PATCH',
      body: data,
    });
  }

  async removeFamilyMember(familyId: string, userId: string) {
    return this.request<{
      success: boolean;
      data: { removed: boolean; left: boolean };
    }>(`/families/${familyId}/members/${userId}`, {
      method: 'DELETE',
    });
  }

  // ---- My invites: accept / decline (D3) ----

  async getMyInvites() {
    return this.request<{ success: boolean; data: MyInvite[] }>('/invites');
  }

  async acceptInvite(inviteId: string) {
    return this.request<{
      success: boolean;
      data: { familyId: string; role: FamilyRole; joined: boolean };
    }>(`/invites/${inviteId}/accept`, { method: 'POST' });
  }

  async declineInvite(inviteId: string) {
    return this.request<{ success: boolean; data: { declined: boolean } }>(
      `/invites/${inviteId}/decline`,
      { method: 'POST' }
    );
  }

  // ---- My devices (passkeys, identity spec §7.7) ----

  /** How this device opened Twinden (M53, D186). */
  async reportAppMode(mode: AppMode) {
    return this.request<{ success: boolean; data: { recorded: boolean } }>(
      '/users/me/app-mode',
      { method: 'POST', body: { mode } }
    );
  }

  async getMyDevices() {
    return this.request<{ success: boolean; data: Device[] }>(
      '/users/me/devices'
    );
  }

  async renameDevice(deviceId: string, deviceName: string) {
    return this.request<{ success: boolean; data: Device }>(
      `/users/me/devices/${deviceId}`,
      { method: 'PATCH', body: { deviceName } }
    );
  }

  async revokeDevice(deviceId: string) {
    return this.request<{ success: boolean; data: { deleted: boolean } }>(
      `/users/me/devices/${deviceId}`,
      { method: 'DELETE' }
    );
  }

  // ---- Signed-in sessions (D212–D214, docs/adding-devices.md) ----

  async getMySessions() {
    return this.request<{ success: boolean; data: SignedInSession[] }>(
      '/users/me/sessions'
    );
  }

  /** Signs one session out; another device's needs a passkey check (D213). */
  async revokeSession(sessionId: string, verification?: unknown) {
    return this.request<{
      success: boolean;
      data: { revoked: number; current: boolean };
    }>(`/users/me/sessions/${sessionId}`, {
      method: 'DELETE',
      body: verification ? { verification } : {},
    });
  }

  /** "Sign out everywhere else" (D213): this device stays signed in. */
  async revokeOtherSessions(verification?: unknown) {
    return this.request<{ success: boolean; data: { revoked: number } }>(
      '/users/me/sessions',
      {
        method: 'DELETE',
        body: {
          exceptCurrent: true,
          ...(verification ? { verification } : {}),
        },
      }
    );
  }

  /** Ask for an invitation to Twinden (M44, public). */
  async requestInvite(data: {
    name: string;
    email: string;
    familySize: string;
    note?: string;
    website?: string;
  }) {
    return this.request<{ success: boolean; data: { received: boolean } }>(
      '/invite-requests',
      { method: 'POST', body: data }
    );
  }

  // ---- Shape Twinden (M55, docs/run-by-families.md) ----

  /** The board; public, with votedByMe and mine when signed in. */
  async getIdeas() {
    return this.request<{
      success: boolean;
      /** truncated: the board is capped (200 live, 50 done, 50 not now). */
      data: { ideas: Idea[]; truncated?: boolean };
    }>('/ideas');
  }

  async postIdea(data: { title: string; body?: string | null }) {
    return this.request<{ success: boolean; data: { idea: Idea } }>('/ideas', {
      method: 'POST',
      body: data,
    });
  }

  async editIdea(id: string, data: { title?: string; body?: string | null }) {
    return this.request<{ success: boolean; data: { idea: Idea } }>(
      `/ideas/${id}`,
      { method: 'PATCH', body: data }
    );
  }

  async voteIdea(id: string, vote: boolean) {
    return this.request<{
      success: boolean;
      data: { votesCount: number; votedByMe: boolean };
    }>(`/ideas/${id}/vote`, { method: vote ? 'PUT' : 'DELETE' });
  }

  /** Public settings for the landing pages (M42): the Chip in link. */
  async getSiteConfig() {
    return this.request<{ success: boolean; data: SiteConfig }>('/site');
  }

  // ---- Adding your own devices (M41, docs/adding-devices.md) ----

  /** Passkey options for a fresh "is it you" check (D91). */
  async verifyStart() {
    return this.request<{ success: boolean; data: Record<string, unknown> }>(
      '/users/me/verify/start',
      { method: 'POST' }
    );
  }

  /**
   * Deletes your account at once and for good (M51, D172), with a fresh
   * passkey check from `verifyStart`. The session is gone afterwards.
   */
  async deleteMyAccount(verification: unknown) {
    await this.request<void>('/users/me', {
      method: 'DELETE',
      body: { verification },
    });
  }

  /**
   * Deletes a family for everyone (M54, D188): a root, with a fresh passkey
   * check from `verifyStart` and the family's name typed exactly.
   */
  async deleteFamily(familyId: string, verification: unknown, name: string) {
    await this.request<void>(`/families/${familyId}`, {
      method: 'DELETE',
      body: { verification, name },
    });
  }

  async createDeviceLink(verification: unknown, familyId?: string | null) {
    return this.request<{
      success: boolean;
      data: { joinUrl: string; qrDataUrl: string; expiresAt: string };
    }>('/users/me/device-links', {
      method: 'POST',
      body: { verification, ...(familyId ? { familyId } : {}) },
    });
  }

  async cancelDeviceLink() {
    return this.request<{ success: boolean; data: { revoked: number } }>(
      '/users/me/device-links',
      { method: 'DELETE' }
    );
  }

  async addPasskeyStart(verification?: unknown, another = false) {
    return this.request<{ success: boolean; data: Record<string, unknown> }>(
      '/users/me/passkeys/start',
      {
        method: 'POST',
        body: {
          ...(verification ? { verification } : {}),
          ...(another ? { another: true } : {}),
        },
      }
    );
  }

  async addPasskeyFinish(response: unknown, deviceName?: string) {
    return this.request<{ success: boolean; data: Device }>(
      '/users/me/passkeys/finish',
      { method: 'POST', body: { response, deviceName } }
    );
  }

  async updateProfile(data: {
    displayName?: string;
    /** null clears it (D144). */
    birthday?: { day: number; month: number } | null;
    /**
     * A sealed picture with its key wrapped for each family (D292), in one
     * write; null removes it.
     */
    picture?: {
      sealed: string;
      keys: Array<{ familyId: string; sealed: string }>;
    } | null;
  }) {
    return this.request<{
      success: boolean;
      data: {
        id: string;
        handle: string;
        displayName: string;
        avatarUrl: string | null;
        /** The current picture's hash, to wrap its key for later (D295). */
        pictureHash?: string | null;
        birthDay: number | null;
        birthMonth: number | null;
      };
    }>('/users/me', { method: 'PATCH', body: data });
  }

  // Public endpoints (no auth needed)
  async checkHandleAvailability(handle: string) {
    const response = await fetch(`${API_BASE}/users/check-handle/${handle}`);
    return response.json() as Promise<{
      success: boolean;
      data: {
        available: boolean;
        reason?: string;
      };
    }>;
  }

  // Passkey login (public, identity spec §7.6)
  async passkeyLoginStart() {
    return this.request<{
      success: boolean;
      data: Record<string, unknown>;
    }>('/auth/passkey/login/start', { method: 'POST' });
  }

  /** Opens a review link (M56, D203): a session with no passkey. */
  async openReviewLink(token: string) {
    return this.request<{
      success: boolean;
      data: AuthResponse & { familyId: string };
    }>(`/review/${encodeURIComponent(token)}`, { method: 'POST' });
  }

  async passkeyLoginFinish(response: unknown) {
    return this.request<{
      success: boolean;
      data: AuthResponse;
    }>('/auth/passkey/login/finish', {
      method: 'POST',
      body: { response },
    });
  }

  // Sign in by scanning (M49, D157): the computer asks for a code, shows it as
  // a QR, and polls with a secret that never leaves this page.
  async createQrSignIn(deviceKind: QrDeviceKind) {
    return this.request<{
      success: boolean;
      data: {
        code: string;
        pollSecret: string;
        expiresAt: string;
        qrDataUrl: string;
        url: string;
      };
    }>('/qr-sign-in', { method: 'POST', body: { deviceKind } });
  }

  async pollQrSignIn(pollSecret: string) {
    return this.request<{
      success: boolean;
      data:
        | { status: 'pending' | 'expired' | 'cancelled' }
        // A phone opened the code: now the computer shows the number (D159).
        | { status: 'scanned'; number: number }
        | ({ status: 'approved' } & AuthResponse);
    }>('/qr-sign-in/poll', { method: 'POST', body: { pollSecret } });
  }

  // The phone's side of sign in by scanning (M49, D159).
  async getQrSignIn(code: string) {
    return this.request<{
      success: boolean;
      data: {
        deviceKind: QrDeviceKind;
        createdAt: string;
        choices: number[];
      };
    }>(`/qr-sign-in/${encodeURIComponent(code)}`);
  }

  async qrSignInVerifyStart(code: string) {
    return this.request<{ success: boolean; data: Record<string, unknown> }>(
      `/qr-sign-in/${encodeURIComponent(code)}/verify-start`,
      { method: 'POST' }
    );
  }

  async approveQrSignIn(code: string, number: number, verification: unknown) {
    return this.request<{ success: boolean; data: { ok: true } }>(
      `/qr-sign-in/${encodeURIComponent(code)}/approve`,
      { method: 'POST', body: { number, verification } }
    );
  }

  async cancelQrSignIn(code: string) {
    return this.request<{ success: boolean; data: { ok: true } }>(
      `/qr-sign-in/${encodeURIComponent(code)}/cancel`,
      { method: 'POST' }
    );
  }

  // Enrollment (public — the token is the auth, identity spec §7.5)
  async getEnrollmentInfo(token: string) {
    return this.request<{
      success: boolean;
      data: {
        kind: 'enroll' | 'add_device';
        familyId: string;
        familyName: string;
        familyHandle: string | null;
        handle: string | null;
        displayName: string | null;
      };
    }>(`/enroll/${token}`);
  }

  /**
   * Fun handles for a new person on a personal link that holds none
   * (D348), signed for this link only (D147).
   */
  async getEnrollmentSuggestions(token: string, gendered = false) {
    const query = gendered ? '?gendered=1' : '';
    return this.request<{
      success: boolean;
      data: { suggestions: HandleSuggestion[] };
    }>(`/enroll/${token}/handle-suggestions${query}`);
  }

  /** `handle`: the pick on a link that holds none (D348), checked early. */
  async enrollPasskeyStart(token: string, handle?: HandleSuggestion) {
    return this.request<{
      success: boolean;
      data: Record<string, unknown>;
    }>(`/enroll/${token}/passkey/start`, {
      method: 'POST',
      body: handle ? { handle: handle.handle, handleToken: handle.token } : {},
    });
  }

  async enrollPasskeyFinish(
    token: string,
    response: unknown,
    deviceName?: string,
    handle?: HandleSuggestion
  ) {
    return this.request<{
      success: boolean;
      data: AuthResponse;
    }>(`/enroll/${token}/passkey/finish`, {
      method: 'POST',
      body: {
        response,
        deviceName,
        ...(handle ? { handle: handle.handle, handleToken: handle.token } : {}),
      },
    });
  }

  // ---- Web Push (M17) ----

  async getPushConfig() {
    return this.request<{ success: boolean; data: PushConfig }>('/push/config');
  }

  async getPushSubscriptions() {
    return this.request<{ success: boolean; data: PushSubscriptionRow[] }>(
      '/push/subscriptions'
    );
  }

  async subscribePush(subscription: {
    endpoint: string;
    keys: { p256dh: string; auth: string };
    userAgent?: string;
  }) {
    return this.request<{ success: boolean; data: { subscribed: boolean } }>(
      '/push/subscriptions',
      { method: 'POST', body: subscription }
    );
  }

  async unsubscribePush(endpoint: string) {
    return this.request<{ success: boolean; data: { subscribed: boolean } }>(
      '/push/subscriptions',
      { method: 'DELETE', body: { endpoint } }
    );
  }

  async getPushPreferences() {
    return this.request<{ success: boolean; data: PushPreferences }>(
      '/push/preferences'
    );
  }

  async updatePushPreferences(update: Partial<PushPreferences>) {
    return this.request<{ success: boolean; data: PushPreferences }>(
      '/push/preferences',
      { method: 'PATCH', body: update }
    );
  }

  async sendTestPush() {
    return this.request<{ success: boolean; data: { devices: number } }>(
      '/push/test',
      { method: 'POST' }
    );
  }

  // ---- Live calls (M19) ----

  async getCallConfig() {
    return this.request<{ success: boolean; data: CallConfig }>(
      '/calls/config'
    );
  }
}

/** A key another device lacks (D57), with that device's keys (M78, §9). */
export interface PendingKeyShare {
  scope: 'chat' | 'family';
  scopeId: string;
  epoch: number;
  deviceKeyId: string;
  publicKey: string;
  /** Absent from a server before M78. */
  userId?: string;
  mlkemPublicKey?: string | null;
}

export class ApiError extends Error {
  constructor(
    message: string,
    public code: string,
    public status: number,
    /** A replacement handle when the one asked for is taken (D147). */
    public suggestion?: HandleSuggestion,
    /** When a capped action is allowed again (D216). */
    public retryAt?: string
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** The server's `error.details`, when it sent any. */
  details?: Record<string, unknown>;
}

export const api = new ApiClient();

// ============================================
// System-root admin API (identity spec §7.2)
// Uses its own token (localStorage 'twinden:adminToken'),
// separate from the user-session tokens above.
// ============================================

const ADMIN_TOKEN_KEY = 'twinden:adminToken';

async function adminRequest<T>(
  endpoint: string,
  options: { method?: string; body?: unknown } = {}
): Promise<T> {
  const token = localStorage.getItem(ADMIN_TOKEN_KEY);
  let response: Response;
  try {
    response = await fetch(`${API_BASE}${endpoint}`, {
      method: options.method ?? 'GET',
      headers: {
        ...(options.body ? { 'Content-Type': 'application/json' } : {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: options.body ? JSON.stringify(options.body) : undefined,
    });
  } catch {
    throw networkError();
  }

  if (!response.ok) {
    throw await toApiError(response);
  }
  return response.json();
}

/**
 * One page of an admin list (#73): newest first, `nextCursor` null on the
 * last page, `total` counts every row the search matches.
 */
export interface AdminPage<T> {
  items: T[];
  nextCursor: string | null;
  total: number;
}

export interface AdminPageParams {
  /** 50 when left out; at most 200. */
  limit?: number;
  cursor?: string | null;
  /** Families and users only: name or handle, any case (#73). */
  q?: string;
}

/** The query string for a page, with any extra parameters. */
export function adminPageQuery(
  params: AdminPageParams = {},
  extra: Record<string, string> = {}
): string {
  const query = new URLSearchParams(extra);
  if (params.limit) query.set('limit', String(params.limit));
  if (params.cursor) query.set('cursor', params.cursor);
  const q = params.q?.trim();
  if (q) query.set('q', q);
  const text = query.toString();
  return text ? `?${text}` : '';
}

export interface AdminDeletedFamily {
  id: string;
  name: string;
  memberCount: number;
  deletedAt: string;
  deletedBy: { id: string; displayName: string } | null;
}

export interface AdminFamily {
  id: string;
  name: string;
  handle: string;
  description: string | null;
  avatarUrl: string | null;
  createdAt: string;
  memberCount: number;
  /** The review family (M56, D202). */
  isDemo?: boolean;
}

export interface AdminEnrollmentToken {
  id: string;
  kind: 'enroll' | 'add_device';
  familyId: string;
  handle: string | null;
  displayName: string | null;
  familyRole: 'root' | 'admin' | 'member';
  userId: string | null;
  status: 'pending' | 'consumed' | 'revoked' | 'expired';
  invitedBy: string | null;
  createdBySystemRoot: boolean;
  expiresAt: string;
  consumedAt: string | null;
  consumedBy: string | null;
  createdAt: string;
}

export interface EnrollmentTokenCreated {
  token: AdminEnrollmentToken;
  joinUrl: string;
  qrDataUrl: string;
}

export interface AdminFamilyDetail {
  id: string;
  name: string;
  handle: string;
  description: string | null;
  avatarUrl: string | null;
  createdAt: string;
  members: Array<{
    id: string;
    userId: string;
    role: 'root' | 'admin' | 'member';
    nickname: string | null;
    joinedAt: string;
    user: {
      id: string;
      handle: string;
      displayName: string;
      avatarUrl: string | null;
      lastSeenAt: string | null;
    };
  }>;
  enrollmentTokens: AdminEnrollmentToken[];
}

export interface AdminUser {
  id: string;
  kind: 'human' | 'app';
  handle: string;
  displayName: string;
  avatarUrl: string | null;
  lastSeenAt: string | null;
  createdAt: string;
  families: Array<{ id: string; name: string; role: string }>;
  deviceCount: number;
  /** How their devices last opened Twinden, where known (D186). */
  appModes: AppMode[];
  /** A demo account for app review (M56, D202). */
  isDemo?: boolean;
}

/** Devices that reported how they opened in the last `days` (D186). */
export interface AdminAppModes {
  days: number;
  installed: number;
  browser: number;
  native: number;
  installedTotal: number;
  known: number;
}

export interface AdminAuditEntry {
  id: string;
  actorUserId: string | null;
  actorIsSystemRoot: boolean;
  action: string;
  targetType: string | null;
  targetId: string | null;
  familyId: string | null;
  metadata: Record<string, unknown> | null;
  createdAt: string;
  // A deleted account has no handle (D173).
  actor?: {
    id: string;
    handle: string | null;
    displayName: string;
    deleted?: true;
  } | null;
}

// Lawful requests (M65, docs/lawful-requests.md D244): an account, live or
// deleted (D245), and its sign-in log (D242). System root only.
export interface LawfulAccount {
  userId: string;
  /** Null for a tombstone from before M65: its handle is gone (D173). */
  handle: string | null;
  displayName: string | null;
  createdAt: string;
  deletedAt: string | null;
}

export interface SignInEvent {
  id: string;
  kind: SignInEventKind;
  at: string;
  ip: string;
  sessionId: string | null;
  client: string | null;
  device: string | null;
}

export interface SignInEventsPage {
  events: SignInEvent[];
  nextBefore: string | null;
  nextBeforeId: string | null;
}

// Monitoring (M67, docs/monitoring.md D256): what the Health tab reads.
export type { HealthErrors, HealthOverview, HealthWindow };

export const adminApi = {
  async login(username: string, password: string) {
    const res = await fetch(`${API_BASE}/admin/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password }),
    });
    if (!res.ok) {
      const error = await res.json().catch(() => ({}));
      throw new ApiError(
        error.error?.message || 'Login failed',
        error.error?.code || 'UNKNOWN_ERROR',
        res.status
      );
    }
    const json = (await res.json()) as {
      success: boolean;
      data: { token: string; expiresIn: string };
    };
    localStorage.setItem(ADMIN_TOKEN_KEY, json.data.token);
    return json.data;
  },

  logout() {
    localStorage.removeItem(ADMIN_TOKEN_KEY);
  },

  isLoggedIn() {
    return !!localStorage.getItem(ADMIN_TOKEN_KEY);
  },

  getFamilies: (params?: AdminPageParams) =>
    adminRequest<{ success: boolean; data: AdminPage<AdminFamily> }>(
      `/admin/families${adminPageQuery(params)}`
    ),

  createFamily: (data: {
    name: string;
    description?: string;
    // No handle: the first root picks theirs on the link page (D348).
    rootDisplayName: string;
  }) =>
    adminRequest<{
      success: boolean;
      data: { family: AdminFamily; enrollment: EnrollmentTokenCreated };
    }>('/admin/families', { method: 'POST', body: data }),

  getFamily: (id: string) =>
    adminRequest<{ success: boolean; data: AdminFamilyDetail }>(
      `/admin/families/${id}`
    ),

  deleteFamily: (id: string) =>
    adminRequest<{ success: boolean; data: { deleted: boolean } }>(
      `/admin/families/${id}`,
      { method: 'DELETE' }
    ),

  // Families their roots deleted (M54, D191).
  getDeletedFamilies: (params?: AdminPageParams) =>
    adminRequest<{ success: boolean; data: AdminPage<AdminDeletedFamily> }>(
      `/admin/deleted-families${adminPageQuery(params)}`
    ),

  createEnrollmentToken: (
    familyId: string,
    input: {
      kind: 'enroll';
      displayName: string;
      familyRole: 'root' | 'admin' | 'member';
      expiresInDays: number;
    }
  ) =>
    adminRequest<{ success: boolean; data: EnrollmentTokenCreated }>(
      `/admin/families/${familyId}/enrollment-tokens`,
      { method: 'POST', body: input }
    ),

  revokeEnrollmentToken: (familyId: string, tokenId: string) =>
    adminRequest<{ success: boolean; data: AdminEnrollmentToken }>(
      `/admin/families/${familyId}/enrollment-tokens/${tokenId}/revoke`,
      { method: 'POST' }
    ),

  regenerateEnrollmentToken: (familyId: string, tokenId: string) =>
    adminRequest<{ success: boolean; data: EnrollmentTokenCreated }>(
      `/admin/families/${familyId}/enrollment-tokens/${tokenId}/regenerate`,
      { method: 'POST' }
    ),

  /** With `id`, just that person (a report's link, D210). */
  getUsers: (params?: AdminPageParams & { id?: string }) =>
    adminRequest<{ success: boolean; data: AdminPage<AdminUser> }>(
      `/admin/users${adminPageQuery(params, params?.id ? { id: params.id } : {})}`
    ),

  getAppModes: () =>
    adminRequest<{ success: boolean; data: AdminAppModes }>('/admin/app-modes'),

  deleteUser: (id: string) =>
    adminRequest<{ success: boolean; data: { deleted: boolean } }>(
      `/admin/users/${id}`,
      { method: 'DELETE' }
    ),

  // App review access (M56, docs/app-review-access.md)
  getReview: () =>
    adminRequest<{ success: boolean; data: AdminReview }>('/admin/review'),

  createReviewFamily: () =>
    adminRequest<{ success: boolean; data: { familyId: string } }>(
      '/admin/review/family',
      { method: 'POST' }
    ),

  addReviewMember: (displayName: string) =>
    adminRequest<{
      success: boolean;
      data: { id: string; handle: string; displayName: string };
    }>('/admin/review/members', { method: 'POST', body: { displayName } }),

  createReviewLink: (userId: string, label?: string) =>
    adminRequest<{
      success: boolean;
      data: { id: string; url: string; expiresAt: string };
    }>('/admin/review/links', {
      method: 'POST',
      body: { userId, ...(label ? { label } : {}) },
    }),

  revokeReviewLink: (id: string) =>
    adminRequest<{ success: boolean; data: { revoked: boolean } }>(
      `/admin/review/links/${id}/revoke`,
      { method: 'POST' }
    ),

  getAuditLog: (params?: AdminPageParams) =>
    adminRequest<{ success: boolean; data: AdminPage<AdminAuditEntry> }>(
      `/admin/audit-log${adminPageQuery(params)}`
    ),

  // Invitation requests (M44, docs/invitation-requests.md)
  /** `newCount` is the tab's badge. */
  getInviteRequests: (params?: AdminPageParams) =>
    adminRequest<{
      success: boolean;
      data: AdminPage<InviteRequest> & { newCount: number };
    }>(`/admin/invite-requests${adminPageQuery(params)}`),

  updateInviteRequest: (
    id: string,
    data: { status?: InviteRequest['status']; note?: string | null }
  ) =>
    adminRequest<{ success: boolean; data: InviteRequest }>(
      `/admin/invite-requests/${id}`,
      { method: 'PATCH', body: data }
    ),

  deleteInviteRequest: (id: string) =>
    adminRequest<{ success: boolean; data: { deleted: boolean } }>(
      `/admin/invite-requests/${id}`,
      { method: 'DELETE' }
    ),

  // Reports (M58, docs/report-block-contact.md D210)
  /** Unhandled first, then newest; `unhandledCount` is the tab's badge. */
  getReports: (
    status: 'unhandled' | 'handled' | 'all' = 'all',
    params?: AdminPageParams
  ) =>
    adminRequest<{
      success: boolean;
      data: AdminPage<AdminReport> & { unhandledCount: number };
    }>(`/admin/reports${adminPageQuery(params, { status })}`),

  markReport: (id: string, handled: boolean) =>
    adminRequest<{ success: boolean; data: { report: AdminReport } }>(
      `/admin/reports/${id}`,
      { method: 'PATCH', body: { handled } }
    ),

  // Shape Twinden (M55, docs/run-by-families.md D198–D200)
  getIdeas: (params?: AdminPageParams) =>
    adminRequest<{ success: boolean; data: AdminPage<AdminIdea> }>(
      `/admin/ideas${adminPageQuery(params)}`
    ),

  updateIdea: (
    id: string,
    data: {
      status?: IdeaStatus;
      note?: string | null;
      title?: string;
      body?: string | null;
      hidden?: boolean;
    }
  ) =>
    adminRequest<{ success: boolean; data: unknown }>(`/admin/ideas/${id}`, {
      method: 'PATCH',
      body: data,
    }),

  mergeIdea: (id: string, targetId: string) =>
    adminRequest<{
      success: boolean;
      data: { targetId: string; votesCount: number };
    }>(`/admin/ideas/${id}/merge`, { method: 'POST', body: { targetId } }),

  // Costs and contributions (M43, docs/costs-and-contributions.md)
  getCosts: () =>
    adminRequest<{ success: boolean; data: CostMonth[] }>('/admin/costs'),

  saveCostMonth: (month: string, data: Omit<CostMonth, 'month'>) =>
    adminRequest<{ success: boolean; data: CostMonth }>(
      `/admin/costs/${month}`,
      { method: 'PUT', body: data }
    ),

  deleteCostMonth: (month: string) =>
    adminRequest<{ success: boolean; data: { deleted: boolean } }>(
      `/admin/costs/${month}`,
      { method: 'DELETE' }
    ),

  // Lawful requests (M65, docs/lawful-requests.md D244)
  // The handle goes in the body, not the URL, so it stays out of host logs.
  lookupAccounts: (query: string) =>
    adminRequest<{
      success: boolean;
      data: { accounts: LawfulAccount[]; retentionDays: number };
    }>('/admin/lawful-requests/lookup', {
      method: 'POST',
      body: { query },
    }),

  getSignInEvents: (
    userId: string,
    cursor?: { before: string; beforeId: string } | null
  ) =>
    adminRequest<{ success: boolean; data: SignInEventsPage }>(
      `/admin/lawful-requests/events?${new URLSearchParams({
        userId,
        ...(cursor ? { before: cursor.before, beforeId: cursor.beforeId } : {}),
      })}`
    ),

  // Monitoring (M67, docs/monitoring.md D256).
  getHealthOverview: (window: HealthWindow) =>
    adminRequest<{ success: boolean; data: HealthOverview }>(
      `/admin/health/overview?window=${window}`
    ),
  getHealthErrors: (window: HealthWindow) =>
    adminRequest<{ success: boolean; data: HealthErrors }>(
      `/admin/health/errors?window=${window}`
    ),

  /** The CSV for a legal request; the server audits every call (D244). */
  async exportSignInEvents(
    userId: string
  ): Promise<{ filename: string; blob: Blob }> {
    const token = localStorage.getItem(ADMIN_TOKEN_KEY);
    let response: Response;
    try {
      response = await fetch(
        `${API_BASE}/admin/lawful-requests/export?${new URLSearchParams({ userId })}`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
    } catch {
      throw networkError();
    }
    if (!response.ok) throw await toApiError(response);
    const disposition = response.headers.get('content-disposition') ?? '';
    const filename =
      /filename="([^"]+)"/.exec(disposition)?.[1] ?? 'twinden-sign-ins.csv';
    return { filename, blob: await response.blob() };
  },
};
